import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { comparison, fmtMinutes, fmtMoney, fmtStops, type FcDestinationResults } from '@/services/FlightCostService';
import './proposal/proposal.css';

type Shared = FcDestinationResults & { assessment_name: string; event_name: string | null; client_name: string | null };

const TEAL = '#1a4d54';
const GOLD = '#f3ad0f';
const CREAM = '#efede7';

/** Public client view of a flight cost estimate: averages per destination and city of origin only, never names. */
export function FlightCostView() {
  const { token = '' } = useParams();
  const [data, setData] = useState<Shared | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Plain fetch: this page is public, so no org header or session refresh.
    const base = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
    fetch(`${base}/api/public/flight-cost/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok || !body?.success) throw new Error(r.status === 404 ? 'This link is no longer available.' : 'Could not load this page.');
        setData(body.data);
      })
      .catch((e) => setError(e.message));
  }, [token]);

  if (error) return <Shell><p className="py-24 text-center" style={{ color: TEAL }}>{error}</p></Shell>;
  if (!data) return <Shell><p className="py-24 text-center opacity-60" style={{ color: TEAL }}>Loading…</p></Shell>;

  const ccy = data.currency.event && data.currency.event !== 'USD' ? data.currency.event : null;
  const { columns, rows } = comparison(data);
  const multiAirport = data.destinations.some((d) => d.airports.length > 1);
  const money = (m: { original: number | null; event: number | null } | null | undefined, big = false) => {
    if (!m || m.original === null) return <span className="opacity-40">—</span>;
    return (
      <span>
        <span className={big ? 'text-3xl font-bold' : 'font-bold'}>{fmtMoney(m.original, 'USD')}</span>
        {ccy && m.event !== null && <span className={big ? 'block text-sm opacity-70 mt-0.5' : 'ml-1.5 text-xs opacity-60'}>{fmtMoney(m.event, ccy)}</span>}
      </span>
    );
  };
  const range = (() => {
    const { out_date: a, back_date: b } = data.inputs;
    if (!a || !b) return '';
    const f = (s: string) => new Date(`${s}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
    return `${f(a)} – ${f(b)}, ${b.slice(0, 4)}`;
  })();

  const single = columns.length === 1;
  const travellers = data.travellers_total ?? rows.reduce((sum, r) => sum + r.passengers, 0);
  const gapFilled = columns.reduce((sum, c) => sum + (c.gap_filled_routes || 0), 0);
  // Summary blocks (Tracey 2026-10-27): travellers once, then per destination airport
  // the average cost, stops on the way there and flight time to the destination.
  const Block = ({ value, label }: { value: ReactNode; label: string }) => (
    <div className="rounded-2xl bg-white px-4 py-5 text-center shadow-sm" style={{ border: `2px solid ${GOLD}`, color: TEAL }}>
      <div className="text-2xl md:text-3xl font-bold leading-tight">{value}</div>
      <div className="mt-1 text-xs md:text-sm font-semibold opacity-80">{label}</div>
    </div>
  );
  const costValue = (m: { original: number | null; event: number | null }) => (m.original === null ? '—' : (
    <>{fmtMoney(m.original, 'USD')}{ccy && m.event !== null && <div className="text-sm font-semibold opacity-70 mt-0.5">{fmtMoney(m.event, ccy)}</div>}</>
  ));
  const destBlocks = (ap: (typeof columns)[number]) => [
    <Block key="c" value={costValue(ap.avg_price)} label="Average per Traveller" />,
    <Block key="s" value={fmtStops(ap.avg_out_stops)} label="Average Stops" />,
    <Block key="t" value={fmtMinutes(ap.avg_out_minutes)} label="Average Flight Time" />,
  ];

  return (
    <Shell>
      <header className="px-6 md:px-12 pt-6 pb-10" style={{ background: TEAL, color: CREAM }}>
        <div className="max-w-5xl mx-auto">
          <img src="/images/moniker_logo_white_full.png" alt="Moniker" className="h-7 md:h-8 object-contain" />
          <div className="mt-8 text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>Flight cost estimate</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">{data.event_name || data.assessment_name}</h1>
          <p className="mt-3 text-sm opacity-80">
            {[range, `${cap(data.inputs.cabin)}, return, per person`].filter(Boolean).join(' · ')}
          </p>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10">
        <div className="max-w-5xl mx-auto space-y-14">
          <section className="space-y-8">
            {single ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Block value={travellers} label="Total Travellers" />
                {destBlocks(columns[0])}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4"><Block value={travellers} label="Total Travellers" /></div>
                {columns.map((ap) => (
                  <div key={ap.key}>
                    <h2 className="font-serif text-2xl" style={{ color: TEAL }}>
                      {ap.destLabel} <span className="text-base opacity-70">· flying into {ap.city || ap.name} ({ap.iata})</span>
                    </h2>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4">{destBlocks(ap)}</div>
                  </div>
                ))}
              </>
            )}
            {single && <p className="text-sm opacity-70" style={{ color: TEAL }}>Flying into {columns[0].city || columns[0].name} ({columns[0].iata})</p>}
          </section>

          <section>
            <h2 className="font-serif text-3xl" style={{ color: TEAL }}>By city of origin</h2>
            {/* Phones: one card per city, every destination stacked (no sideways scrolling). */}
            <div className="mt-5 space-y-3 md:hidden" style={{ color: TEAL }}>
              {rows.map((r) => (
                <div key={`m-${r.city}|${r.country}`} className="rounded-lg bg-white shadow-sm p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-bold">{r.city}{r.country ? <span className="font-normal opacity-60">, {r.country}</span> : null} <span className="ml-1 text-xs font-semibold tracking-wide opacity-70">{r.airport}</span></div>
                    <div className="text-xs opacity-60 whitespace-nowrap">{r.passengers} traveller{r.passengers === 1 ? '' : 's'}</div>
                  </div>
                  <div className="mt-2 divide-y" style={{ borderColor: CREAM }}>
                    {columns.map((c) => {
                      const cell = r.cells[c.key];
                      return (
                        <div key={c.key} className="flex items-start justify-between gap-3 py-2">
                          <div className="text-xs uppercase tracking-wider opacity-70 pt-0.5">{multiAirport ? `${c.destLabel} (${c.iata})` : c.destLabel}</div>
                          <div className="text-right text-sm">
                            {cell?.price ? <>{money(cell.price)}<div className="text-xs opacity-60">{fmtStops(cell.outStops)} stops · {fmtMinutes(cell.outMinutes)}</div></> : <span className="opacity-40">—</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-lg bg-white shadow-sm overflow-x-auto hidden md:block">
              <table className="w-full text-sm" style={{ color: TEAL }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${CREAM}` }}>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider opacity-70">City of origin</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider opacity-70">Airport</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider opacity-70">Travellers</th>
                    {columns.map((c) => <th key={c.key} className="text-left px-4 py-3 text-xs uppercase tracking-wider opacity-70 whitespace-nowrap">{multiAirport ? `${c.destLabel} (${c.iata})` : c.destLabel}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.city}|${r.country}`} style={{ borderBottom: `1px solid ${CREAM}` }}>
                      <td className="px-4 py-2.5 whitespace-nowrap">{r.city}{r.country ? <span className="opacity-60">, {r.country}</span> : null}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap font-semibold tracking-wide">{r.airport}</td>
                      <td className="px-4 py-2.5">{r.passengers}</td>
                      {columns.map((c) => {
                        const cell = r.cells[c.key];
                        return (
                          <td key={c.key} className="px-4 py-2.5 whitespace-nowrap">
                            {cell?.price ? <>{money(cell.price)}<div className="text-xs opacity-60">{fmtStops(cell.outStops)} stops · {fmtMinutes(cell.outMinutes)}</div></> : <span className="opacity-40">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <p className="text-xs leading-relaxed opacity-70" style={{ color: TEAL }}>
            Estimates are based on published economy fares found for these dates, averaging the five lowest fares per route
            {data.inputs.exclude_basic_fares ? ' and leaving out basic fares' : ''}. Prices change until booking and are not a quote.
            {' '}Stops and flight times are for the flight to the destination, including connections.
            {ccy && data.currency.fx_rate ? ` ${ccy} amounts use 1 USD = ${data.currency.fx_rate.toFixed(4)} ${ccy}.` : ''}
            {gapFilled > 0 ? ` ${gapFilled} route${gapFilled === 1 ? ' was' : 's were'} priced from Google Flights, where basic fares cannot be left out.` : ''}
          </p>
        </div>
      </main>
    </Shell>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

function Shell({ children }: { children: ReactNode }) {
  return <div className="proposal-container min-h-screen" style={{ background: CREAM }}>{children}</div>;
}
